import { useMutation, useQuery } from "@apollo/client";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SyncIcon from "@mui/icons-material/Sync";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Dialog as MuiDialog,
  Select,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FilePreview } from "components/Document/shared/FilePreview";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { gql } from "generated-graphql";
import {
  BulkManifestDiscrepanciesQuery,
  DiscrepancyKind,
  DocumentType,
} from "generated-graphql/graphql";
import { useCtrlWheelHorizontalScroll } from "hooks/useCtrlWheelHorizontalScroll";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";
import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { CREATE_EXCEPTION_REPORT_SUBMISSION, PROCESSED_MANIFESTS_QUERY } from "./api";
import { CompliancePane } from "./CompliancePane";
import {
  type ComplianceItem,
  type ComplianceItemMissing,
  getDeadlineBand,
  mapEpaCorrectionDiscrepancy,
} from "./CompliancePane.helpers";
import {
  ALL_DISCREPANCY_STATUSES,
  type DiscrepancyStatus,
  formatDateOnly,
  formatDisplayValue,
  getDiscrepancyStatus,
  getFieldLabel,
  getStatusChipIcon,
  getStatusChipTooltip,
  getStatusColor,
  getStatusLabel,
  isComplianceDiscrepancy,
  isDiscrepancyStatus,
} from "./DiscrepancyReviewDialog.helpers";
import { ProofOfContactDialog } from "./ProofOfContactDialog";
import { useRangeSelection } from "./useRangeSelection";

export const BULK_MANIFEST_DISCREPANCIES_QUERY = gql(`
  query BulkManifestDiscrepancies($processedManifestIds: [ID!]!) {
    bulkManifestDiscrepancies(processedManifestIds: $processedManifestIds) {
      id
      processedManifestId
      manifestTrackingNumber
      facilityName
      epaId
      wasteLineUnifiedId
      wasteLineNumber
      field
      isDot
      kind
      severity
      status
      eManifestValue
      vendorValue
      manifestDocumentValue
      sourceDocumentId
      sourceDocumentFileExtension
      escalation
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionType
      resolutionNote
      createdAt
      wasteManifestCorrectionRequest {
        id
        status
        requestDate
        industryRespondedAt
        initiatorRole
        sections
      }
    }
  }
`);

const UPDATE_DISCREPANCY_VENDOR_DATA = gql(`
  mutation UpdateDiscrepancyVendorData($discrepancyIds: [ID!]!) {
    updateDiscrepancyVendorData(discrepancyIds: $discrepancyIds) {
      id
      processedManifestId
      field
      status
      eManifestValue
      vendorValue
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionType
      resolutionNote
    }
  }
`);

const RESOLVE_DISCREPANCIES_EXTERNALLY = gql(`
  mutation ResolveDiscrepanciesExternally($discrepancyIds: [ID!]!) {
    resolveDiscrepanciesExternally(discrepancyIds: $discrepancyIds) {
      id
      processedManifestId
      field
      status
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionType
      resolutionNote
    }
  }
`);

const DISMISS_DISCREPANCIES = gql(`
  mutation DismissDiscrepancies($discrepancyIds: [ID!]!, $note: String) {
    dismissDiscrepancies(discrepancyIds: $discrepancyIds, note: $note) {
      id
      processedManifestId
      field
      status
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionType
      resolutionNote
    }
  }
`);

export const COMPLIANCE_MANIFEST_DATA_QUERY = gql(`
  query ComplianceManifestData($search: String, $page: Int, $pageSize: Int) {
    processedManifests(search: $search, page: $page, pageSize: $pageSize) {
      items {
        id
        shippedDate
        desFacilityName
        transporterContactedAt
        transporterContactedBy
        documents {
          document {
            id
            documentType
          }
        }
        exceptionReportSubmission {
          id
          title
          status
        }
      }
    }
  }
`);

// rem-based so the sticky offset tracks root font-size (≈37px at 16px root, the small-table header height).
const STICKY_COLUMN_HEADER_HEIGHT = "2.3125rem";
const STICKY_SECTION_HEADER_TOP = STICKY_COLUMN_HEADER_HEIGHT;

// Mirrors BLOCKED_FIELDS in apps/api/src/resolvers/waste/processedManifestCompliance.ts.
const UPDATE_VENDOR_DATA_BLOCKED_FIELDS = new Set([
  "eManifestMissing",
  "wasteLinesCount",
  "shippedDate",
  "destinationFacilityId",
]);

type ActionKey = "updateVendorData" | "resolveExternally" | "nonDiscrepancy";

type SkipGroup = {
  reason: string;
  count: number;
};

type ActionEligibility = {
  eligibleIds: string[];
  skips: SkipGroup[];
};

function computeActionEligibility(
  action: ActionKey,
  selected: readonly Discrepancy[]
): ActionEligibility {
  const eligibleIds: string[] = [];
  const skipCounts = new Map<string, number>();
  const addSkip = (reason: string) => {
    skipCounts.set(reason, (skipCounts.get(reason) ?? 0) + 1);
  };

  for (const d of selected) {
    if (action === "updateVendorData") {
      if (d.status !== "UNRESOLVED") {
        addSkip("already resolved or pending");
      } else if (UPDATE_VENDOR_DATA_BLOCKED_FIELDS.has(d.field)) {
        addSkip(`${getFieldLabel(d.field)} doesn't support vendor data update`);
      } else {
        eligibleIds.push(d.id);
      }
    } else if (action === "resolveExternally") {
      if (d.status === "RESOLVED") {
        addSkip("already resolved");
      } else if (d.status === "PENDING") {
        addSkip("already pending");
      } else {
        eligibleIds.push(d.id);
      }
    } else {
      if (d.status === "RESOLVED" || d.status === "IGNORED") {
        addSkip("already resolved");
      } else if (isComplianceDiscrepancy(d)) {
        addSkip("compliance row — use Resolve in e-Manifest instead");
      } else {
        eligibleIds.push(d.id);
      }
    }
  }

  const skips = [...skipCounts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);

  return { eligibleIds, skips };
}

function formatSkipSummary(skips: SkipGroup[]): string {
  return skips.map((s) => `• ${s.count} ${s.reason}`).join("\n");
}

type Discrepancy = BulkManifestDiscrepanciesQuery["bulkManifestDiscrepancies"][number];

function SectionHeaderRow({
  label,
  count,
  colSpan,
  collapsed,
  onToggle,
}: {
  label: string;
  count: number;
  colSpan: number;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // MUI's `grey` palette is mode-independent — flip to darker shades in dark mode.
  const subtleBg = isDark ? theme.palette.grey[900] : theme.palette.grey[100];
  const subtleHover = isDark ? theme.palette.grey[800] : theme.palette.grey[200];
  const subtleBorder = isDark ? theme.palette.grey[800] : theme.palette.grey[200];
  return (
    <TableRow
      onClick={onToggle}
      sx={{
        cursor: "pointer",
        "&:hover": { bgcolor: subtleHover },
      }}
    >
      <TableCell
        colSpan={colSpan}
        sx={{
          bgcolor: subtleBg,
          py: 0.75,
          px: 0,
          fontWeight: 600,
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${subtleBorder}`,
          position: "sticky",
          top: STICKY_SECTION_HEADER_TOP,
          zIndex: 1,
        }}
      >
        {/* Inner sticky wrapper pins the label + chevron to the left edge during horizontal scroll. */}
        <Box
          sx={{
            position: "sticky",
            left: 0,
            width: "fit-content",
            px: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {collapsed ? (
              <ExpandMoreIcon sx={{ fontSize: 18 }} />
            ) : (
              <ExpandLessIcon sx={{ fontSize: 18 }} />
            )}
            <span>{label}</span>
            <Chip size="small" label={count} sx={{ height: 20, fontSize: "0.75rem" }} />
          </Stack>
        </Box>
      </TableCell>
    </TableRow>
  );
}

// Plain data (not a callback) so the confirm handler reads confirmNote at confirm-time, not click-time.
type ConfirmActionState = {
  label: string;
  description: string;
  action: ActionKey;
  eligibleIds: string[];
};

interface BulkActionButtonProps {
  label: string;
  icon: React.ReactElement;
  baseDescription: string;
  eligibility: ActionEligibility;
  totalSelected: number;
  loading: boolean;
  action: ActionKey;
  setConfirmAction: (action: ConfirmActionState | null) => void;
}

function BulkActionButton({
  label,
  icon,
  baseDescription,
  eligibility,
  totalSelected,
  loading,
  action,
  setConfirmAction,
}: BulkActionButtonProps) {
  const { eligibleIds, skips } = eligibility;
  const eligibleCount = eligibleIds.length;
  const skipCount = totalSelected - eligibleCount;
  const nothingSelected = totalSelected === 0;
  const nothingEligible = totalSelected > 0 && eligibleCount === 0;

  let tooltipTitle = "";
  if (nothingSelected) {
    tooltipTitle = "";
  } else if (nothingEligible) {
    tooltipTitle = `None of the ${totalSelected} selected rows support ${label}.\n${formatSkipSummary(skips)}`;
  } else if (skipCount > 0) {
    tooltipTitle = `${eligibleCount} of ${totalSelected} will run. ${skipCount} will be skipped:\n${formatSkipSummary(skips)}`;
  }

  const rowWord = (n: number) => (n === 1 ? "row" : "rows");
  const description =
    skipCount > 0
      ? `${baseDescription}\n\n${eligibleCount} of ${totalSelected} selected ${rowWord(totalSelected)} will be processed. ${skipCount} will be skipped:\n${formatSkipSummary(skips)}`
      : `${baseDescription}\n\nThis will apply to ${eligibleCount} selected ${rowWord(eligibleCount)}.`;

  return (
    <Tooltip
      title={tooltipTitle}
      placement="top"
      enterDelay={300}
      slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
    >
      <span>
        <Button
          variant="outlined"
          size="small"
          color="warning"
          startIcon={icon}
          disabled={nothingSelected || nothingEligible || loading}
          onClick={() =>
            setConfirmAction({
              label,
              description,
              action,
              eligibleIds,
            })
          }
        >
          {label}
        </Button>
      </span>
    </Tooltip>
  );
}

interface DiscrepancyReviewDialogProps {
  processedManifestIds: string[];
  manifestTrackingNumber?: string;
  wasteLineUnifiedIds?: string[];
  onClose: () => void;
}

export function DiscrepancyReviewDialog({
  processedManifestIds,
  manifestTrackingNumber,
  wasteLineUnifiedIds,
  onClose,
}: DiscrepancyReviewDialogProps) {
  const { tenantId } = useParams<{ tenantId: string }>();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // Dark mode: transparent so Dialog's elevation overlay shows through. A literal background.paper
  // would render as a black slab because Dialog paints its grey via overlay, not the paper color.
  const panelBg = isDark ? "transparent" : theme.palette.background.paper;
  // Sticky surfaces need to be opaque; replicate Paper's elevation-24 overlay (16% white) in dark mode.
  const stickyOpaquePanelBg = isDark
    ? {
        backgroundColor: theme.palette.background.paper,
        backgroundImage: `linear-gradient(${alpha(
          theme.palette.common.white,
          0.16
        )}, ${alpha(theme.palette.common.white, 0.16)})`,
      }
    : { backgroundColor: theme.palette.background.paper };
  const subtleSurfaceBg = isDark ? theme.palette.grey[900] : theme.palette.grey[50];
  const rowDividerColor = isDark ? theme.palette.grey[800] : theme.palette.grey[100];
  const alerts = useAlerts();
  const [statusFilter, setStatusFilter] = useState<DiscrepancyStatus[]>(() => [
    ...ALL_DISCREPANCY_STATUSES,
  ]);

  const tableScrollRef = useCtrlWheelHorizontalScroll();

  // Compliance is the primary surface; users can switch to Field manually.
  const [activeTab, setActiveTab] = useState<"field" | "compliance">("compliance");

  // Stable "now" so deadline math doesn't jitter across re-renders during the same dialog session.
  const nowRef = useMemo(() => new Date(), []);

  // Source of truth: the document the user asked to preview. Apollo's keyed useQuery discards
  // in-flight responses when the target changes, so a slow fetch can't clobber a newer selection.
  const [previewTarget, setPreviewTarget] = useState<{
    documentId: string;
    fileExtension: string;
  } | null>(null);

  const { data: previewUrlData, loading: previewUrlLoading } = useQuery(
    GET_DOCUMENT_DOWNLOAD_LINK_QUERY,
    {
      variables: { id: previewTarget?.documentId ?? "" },
      skip: !previewTarget,
      fetchPolicy: "network-only",
      onError: () => {
        // Clear the target so previewLoading doesn't hang the panel in its skeleton forever.
        alerts.error("Failed to load document preview");
        setPreviewTarget(null);
      },
    }
  );

  const previewDoc = useMemo(() => {
    if (!previewTarget) return null;
    if (previewUrlLoading) return null;
    const url = previewUrlData?.getDocumentDownloadLink;
    if (!url) return null;
    return {
      documentId: previewTarget.documentId,
      fileExtension: previewTarget.fileExtension,
      url,
    };
  }, [previewTarget, previewUrlLoading, previewUrlData]);

  const previewLoading = previewTarget != null && previewDoc == null;

  // Always the target's id (not previewDoc's) so row highlights activate synchronously on click.
  const activePreviewDocId = previewTarget?.documentId ?? null;

  const openDocumentPreview = useCallback(
    (documentId: string, fileExtension: string | null | undefined) => {
      // Functional setter keeps the same-doc no-op guard without depending on previewTarget,
      // so the callback identity stays stable across preview changes.
      setPreviewTarget((prev) =>
        prev?.documentId === documentId
          ? prev
          : { documentId, fileExtension: fileExtension ?? "pdf" }
      );
    },
    []
  );

  const openDocumentInNewTab = useCallback(() => {
    if (!previewDoc?.url) return;
    window.open(previewDoc.url, "_blank");
  }, [previewDoc]);

  // Operation name strings refetch all active instances regardless of variable differences.
  const parentRefetchQueries = ["ProcessedManifests", "ComplianceManifestData"];

  const [updateVendorData, { loading: updatingVendorData }] = useMutation(
    UPDATE_DISCREPANCY_VENDOR_DATA,
    { refetchQueries: parentRefetchQueries }
  );

  const [resolveExternally, { loading: resolvingExternally }] = useMutation(
    RESOLVE_DISCREPANCIES_EXTERNALLY,
    { refetchQueries: parentRefetchQueries }
  );

  const [dismiss, { loading: dismissing }] = useMutation(DISMISS_DISCREPANCIES, {
    // IGNORED items are filtered server-side; refetch so they drop from the list.
    refetchQueries: [...parentRefetchQueries, "BulkManifestDiscrepancies"],
  });

  const mutationLoading = updatingVendorData || resolvingExternally || dismissing;

  const [confirmAction, setConfirmAction] = useState<ConfirmActionState | null>(null);
  const [confirmNote, setConfirmNote] = useState("");

  const { data, loading } = useQuery(BULK_MANIFEST_DISCREPANCIES_QUERY, {
    variables: { processedManifestIds },
  });

  const compliancePmIds = useMemo(() => {
    const raw = data?.bulkManifestDiscrepancies ?? [];
    const ids = new Set<string>();
    for (const d of raw) {
      if (isComplianceDiscrepancy(d)) {
        ids.add(d.processedManifestId);
      }
    }
    return [...ids];
  }, [data?.bulkManifestDiscrepancies]);

  const { data: complianceData } = useQuery(COMPLIANCE_MANIFEST_DATA_QUERY, {
    variables: {
      search: `tenantId:${tenantId} id:${compliancePmIds.join("⁋")}`,
      page: 0,
      pageSize: compliancePmIds.length,
    },
    skip: compliancePmIds.length === 0 || !tenantId,
  });

  const complianceManifestMap = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<typeof complianceData>["processedManifests"]["items"][number]
    >();
    for (const item of complianceData?.processedManifests?.items ?? []) {
      map.set(item.id, item);
    }
    return map;
  }, [complianceData]);

  const [createExceptionReport, { loading: creatingExceptionReport }] = useMutation(
    CREATE_EXCEPTION_REPORT_SUBMISSION,
    {
      refetchQueries: [
        PROCESSED_MANIFESTS_QUERY,
        BULK_MANIFEST_DISCREPANCIES_QUERY,
        COMPLIANCE_MANIFEST_DATA_QUERY,
      ],
    }
  );

  const [showProofOfContactForPmId, setShowProofOfContactForPmId] = useState<string | null>(null);

  const { featureFlags } = useFeatureFlags();
  const showEpaCorrectionRows = !!featureFlags[FeatureFlagKey.WasteEpaCorrectionDiscrepancies];

  const allDiscrepancies = useMemo(() => {
    const raw = data?.bulkManifestDiscrepancies ?? [];
    // Hide EPA correction rows when the flag is off, in case any were seeded during a flag-on window.
    const kindFiltered = showEpaCorrectionRows
      ? raw
      : raw.filter((d) => d.kind !== DiscrepancyKind.EpaCorrectionRequest);
    if (!wasteLineUnifiedIds) return kindFiltered;
    const wluSet = new Set(wasteLineUnifiedIds);
    return kindFiltered.filter((d) => d.wasteLineUnifiedId && wluSet.has(d.wasteLineUnifiedId));
  }, [data?.bulkManifestDiscrepancies, wasteLineUnifiedIds, showEpaCorrectionRows]);

  const complianceItems = useMemo<ComplianceItem[]>(() => {
    const items: ComplianceItem[] = [];
    for (const d of allDiscrepancies) {
      const status = getDiscrepancyStatus(d);
      if (isComplianceDiscrepancy(d)) {
        const pm = complianceManifestMap.get(d.processedManifestId);
        const proofDoc = pm?.documents?.find(
          (link) => link.document.documentType === DocumentType.WasteTransporterContactProof
        )?.document;
        const missing: ComplianceItemMissing = {
          kind: "missing",
          id: d.id,
          processedManifestId: d.processedManifestId,
          manifestTrackingNumber: d.manifestTrackingNumber ?? null,
          epaId: d.epaId ?? null,
          generatorName: d.facilityName ?? null,
          tsdfName: pm?.desFacilityName ?? null,
          sourceDocumentId: d.sourceDocumentId ?? null,
          sourceDocumentFileExtension: d.sourceDocumentFileExtension ?? null,
          status,
          escalation: d.escalation ?? "",
          shippedDate: pm?.shippedDate ?? null,
          transporterContactedAt: pm?.transporterContactedAt ?? null,
          transporterContactedBy: pm?.transporterContactedBy ?? null,
          transporterContactProofDocumentId: proofDoc?.id ?? null,
          exceptionReportSubmissionId: pm?.exceptionReportSubmission?.id ?? null,
          exceptionReportTitle: pm?.exceptionReportSubmission?.title ?? null,
        };
        items.push(missing);
        continue;
      }
      if (d.kind === DiscrepancyKind.EpaCorrectionRequest) {
        const epa = mapEpaCorrectionDiscrepancy({ ...d, status });
        if (epa) {
          // The pure helper doesn't see Apollo data; apply tsdfName from the compliance pm fetch.
          const pm = complianceManifestMap.get(d.processedManifestId);
          items.push({ ...epa, tsdfName: pm?.desFacilityName ?? null });
        }
      }
    }
    return items;
  }, [allDiscrepancies, complianceManifestMap]);

  const filteredComplianceItems = useMemo(() => {
    if (statusFilter.length === 0) return [];
    return complianceItems.filter((i) => statusFilter.includes(i.status));
  }, [complianceItems, statusFilter]);

  const isLineItemMode = Boolean(wasteLineUnifiedIds);
  const isSingleManifest = processedManifestIds.length === 1;

  let title: string;
  if (isLineItemMode) {
    title = `Line Item Discrepancies — ${wasteLineUnifiedIds?.length ?? 0} line items`;
  } else if (isSingleManifest) {
    title = `Manifest Discrepancies — #${manifestTrackingNumber ?? processedManifestIds[0].slice(0, 8)}`;
  } else {
    title = `Bulk Discrepancy Review — ${processedManifestIds.length} manifests`;
  }

  // Only kind=FIELD rows belong in the Field tab; other kinds render in Compliance.
  const discrepancies = useMemo(() => {
    if (statusFilter.length === 0) return [];
    return allDiscrepancies.filter(
      (d) => d.kind === DiscrepancyKind.Field && statusFilter.includes(getDiscrepancyStatus(d))
    );
  }, [allDiscrepancies, statusFilter]);

  const {
    selectedIds,
    toggle: toggleSelect,
    clear: clearSelection,
  } = useRangeSelection({ items: discrepancies });

  // Reads confirmNote at invocation-time — the user types it AFTER opening the dialog,
  // so binding the action at click-time would freeze it and silently drop the note.
  const handleConfirm = useCallback(async () => {
    if (!confirmAction) return;
    const { action, eligibleIds, label } = confirmAction;
    const note = confirmNote || null;
    try {
      if (action === "updateVendorData") {
        await updateVendorData({ variables: { discrepancyIds: eligibleIds } });
        alerts.success(`${eligibleIds.length} discrepancies resolved — vendor data updated`);
      } else if (action === "resolveExternally") {
        await resolveExternally({ variables: { discrepancyIds: eligibleIds } });
        alerts.success(
          `${eligibleIds.length} discrepancies marked as pending e-Manifest resolution`
        );
      } else {
        await dismiss({ variables: { discrepancyIds: eligibleIds, note } });
        alerts.success(`${eligibleIds.length} items dismissed as non-discrepancies`);
      }
      clearSelection();
    } catch (err) {
      alerts.error(`Failed to ${label.toLowerCase()}`, err instanceof Error ? err : undefined);
    }
    setConfirmAction(null);
    setConfirmNote("");
  }, [
    confirmAction,
    confirmNote,
    updateVendorData,
    resolveExternally,
    dismiss,
    alerts,
    clearSelection,
  ]);

  const fieldGroups = useMemo(() => {
    const groups = new Map<string, Discrepancy[]>();
    for (const d of discrepancies) {
      const existing = groups.get(d.field);
      if (existing) {
        existing.push(d);
      } else {
        groups.set(d.field, [d]);
      }
    }
    return [...groups.entries()].map(([field, items]) => ({
      field,
      label: getFieldLabel(field),
      items,
    }));
  }, [discrepancies]);

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (field: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(field)) {
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  };

  const selectedDiscrepancies = useMemo(
    () => discrepancies.filter((d) => selectedIds.has(d.id)),
    [discrepancies, selectedIds]
  );

  const updateVendorDataEligibility = useMemo(
    () => computeActionEligibility("updateVendorData", selectedDiscrepancies),
    [selectedDiscrepancies]
  );
  const resolveExternallyEligibility = useMemo(
    () => computeActionEligibility("resolveExternally", selectedDiscrepancies),
    [selectedDiscrepancies]
  );
  const nonDiscrepancyEligibility = useMemo(
    () => computeActionEligibility("nonDiscrepancy", selectedDiscrepancies),
    [selectedDiscrepancies]
  );

  const COL_COUNT = 7; // checkbox, facility, manifest, eManifest, vendor, document, status

  const renderValueCell = (
    value: string | null | undefined,
    discrepancy: Discrepancy,
    source: "eManifest" | "vendor"
  ) => {
    const display =
      discrepancy.field === "shippedDate" ? formatDateOnly(value) : formatDisplayValue(value);
    return (
      <TableCell
        sx={{
          maxWidth: 240,
          wordBreak: "break-word",
        }}
      >
        <Typography variant="body2" sx={{ pt: "4px" }}>
          {display}
        </Typography>
      </TableCell>
    );
  };

  const renderDiscrepancyRow = (d: Discrepancy) => {
    const status = getDiscrepancyStatus(d);
    const isSelected = selectedIds.has(d.id);
    const isPreviewedDoc =
      activePreviewDocId != null &&
      d.sourceDocumentId != null &&
      d.sourceDocumentId === activePreviewDocId;

    // Row background precedence: selected > previewed doc > default. Divider tracks the background.
    let baseBgcolor: string | undefined;
    let hoverBgcolor: string;
    let dividerColor: string;
    if (isSelected) {
      baseBgcolor = alpha(theme.palette.primary.main, 0.2);
      hoverBgcolor = alpha(theme.palette.primary.main, 0.24);
      dividerColor = alpha(theme.palette.primary.main, 0.1);
    } else if (isPreviewedDoc) {
      baseBgcolor = alpha(theme.palette.primary.main, 0.08);
      hoverBgcolor = alpha(theme.palette.primary.main, 0.12);
      dividerColor = alpha(theme.palette.primary.main, 0.1);
    } else {
      baseBgcolor = undefined;
      hoverBgcolor = theme.palette.action.hover;
      dividerColor = rowDividerColor;
    }

    return (
      <TableRow
        key={d.id}
        selected={isSelected}
        onMouseDown={(event) => {
          // Suppress native shift-click text-selection across rows; non-shift selection still works.
          if (event.shiftKey) {
            event.preventDefault();
          }
        }}
        onClick={(event) => {
          // Let interactive descendants handle their own clicks before row selection.
          if (
            event.target instanceof Element &&
            event.target.closest('button, a, input, [role="button"]')
          ) {
            return;
          }
          toggleSelect(d.id, { shiftKey: event.shiftKey });
        }}
        sx={{
          cursor: "pointer",
          "& .MuiTableCell-body": {
            borderBottomColor: dividerColor,
          },
          ...(baseBgcolor != null ? { bgcolor: baseBgcolor } : {}),
          // Override MUI's Mui-selected tinting so primary wins over action.selected.
          ...(isSelected
            ? {
                "&.Mui-selected": { bgcolor: baseBgcolor },
                "&.Mui-selected:hover": { bgcolor: hoverBgcolor },
              }
            : {}),
          "&:hover": {
            bgcolor: hoverBgcolor,
          },
        }}
      >
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={(e) => {
              // shiftKey lives on the native event, not the synthetic ChangeEvent.
              const nativeEvt = e.nativeEvent;
              const shiftKey =
                nativeEvt instanceof MouseEvent || nativeEvt instanceof KeyboardEvent
                  ? nativeEvt.shiftKey
                  : false;
              toggleSelect(d.id, { shiftKey });
            }}
            sx={{ mt: "12px" }}
          />
        </TableCell>

        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <Typography variant="body2" sx={{ pt: "4px" }}>
            {d.facilityName ?? "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {d.epaId}
          </Typography>
        </TableCell>

        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <Typography variant="body2" fontWeight={500} sx={{ pt: "4px" }}>
            {d.manifestTrackingNumber ?? d.processedManifestId.slice(0, 8)}
          </Typography>
          {d.wasteLineUnifiedId && (
            <Typography variant="caption" color="text.secondary">
              Line {d.wasteLineNumber ?? "item"}
            </Typography>
          )}
        </TableCell>

        {renderValueCell(d.eManifestValue, d, "eManifest")}
        {renderValueCell(d.vendorValue, d, "vendor")}

        <TableCell sx={{ maxWidth: 240, wordBreak: "break-word" }}>
          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
            {d.sourceDocumentId && (
              <Tooltip title={isPreviewedDoc ? "Viewing document" : "Preview source document"}>
                <IconButton
                  size="small"
                  sx={{ pl: 0 }}
                  onClick={() => {
                    if (d.sourceDocumentId) {
                      openDocumentPreview(d.sourceDocumentId, d.sourceDocumentFileExtension);
                    }
                  }}
                >
                  {isPreviewedDoc ? (
                    <VisibilityIcon sx={{ fontSize: 16 }} color="primary" />
                  ) : (
                    <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Typography variant="body2" sx={{ pt: "4px" }}>
              {d.field === "shippedDate"
                ? formatDateOnly(d.manifestDocumentValue)
                : formatDisplayValue(d.manifestDocumentValue)}
            </Typography>
          </Stack>
        </TableCell>

        <TableCell>
          {(() => {
            const tooltipText = getStatusChipTooltip(d, status);
            const statusIcon = getStatusChipIcon(d, status);
            // Use Chip's icon slot (decorative); deleteIcon would be announced as a button by AT.
            const chip = (
              <Chip
                size="small"
                label={getStatusLabel(status)}
                color={getStatusColor(status)}
                variant="outlined"
                icon={
                  statusIcon ? React.cloneElement(statusIcon, { "aria-hidden": true }) : undefined
                }
                sx={{
                  cursor: "default",
                  mt: "2px",
                  flexDirection: "row-reverse",
                  bgcolor: "background.paper",
                  "& .MuiChip-icon": {
                    fontSize: 14,
                    opacity: 0.7,
                    // row-reverse swaps left/right — pull icon-left margins to the right side.
                    marginRight: "5px",
                    marginLeft: "-2px",
                  },
                }}
              />
            );
            return tooltipText ? <Tooltip title={tooltipText}>{chip}</Tooltip> : chip;
          })()}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth={(previewDoc ?? previewLoading) ? false : "xl"}
        fullWidth
        slotProps={{
          paper: {
            sx: {
              maxHeight: "90vh",
              height: "90vh",
              ...((previewDoc ?? previewLoading) ? { maxWidth: "95vw", width: "95vw" } : {}),
            },
          },
        }}
        title={title}
        noPrimaryAction
        titleBarActionElement={(() => {
          const allSelectedInFilter = ALL_DISCREPANCY_STATUSES.every((s) =>
            statusFilter.includes(s)
          );
          const allStatuses = [...ALL_DISCREPANCY_STATUSES];
          // Typing Select as <string[]> accommodates the "__all__" sentinel without an as-cast.
          return (
            <FormControl size="small" sx={{ width: 220, position: "relative" }}>
              <InputLabel>Status</InputLabel>
              <Select<string[]>
                multiple
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  const raw = e.target.value;
                  // MUI emits a comma-joined string in native-select mode; otherwise it's a string[].
                  const rawArray: string[] = typeof raw === "string" ? raw.split(",") : raw;

                  if (rawArray.includes("__all__")) {
                    setStatusFilter(allSelectedInFilter ? [] : allStatuses);
                    return;
                  }

                  setStatusFilter(rawArray.filter(isDiscrepancyStatus));
                }}
                displayEmpty
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return "None selected";
                  }
                  if (selected.length === ALL_DISCREPANCY_STATUSES.length) {
                    return "All selected";
                  }
                  return selected
                    .filter(isDiscrepancyStatus)
                    .map((s) => getStatusLabel(s))
                    .join(", ");
                }}
              >
                <MenuItem value="__all__">
                  <Checkbox
                    size="small"
                    checked={allSelectedInFilter}
                    indeterminate={!allSelectedInFilter && statusFilter.length > 0}
                  />
                  <ListItemText primary="Select all" />
                </MenuItem>
                {ALL_DISCREPANCY_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    <Checkbox size="small" checked={statusFilter.includes(s)} />
                    <ListItemText primary={getStatusLabel(s)} />
                  </MenuItem>
                ))}
              </Select>
              {statusFilter.length === 0 && (
                <FormHelperText
                  sx={{
                    // Float out of flow so wrapping copy doesn't push the dialog content downward.
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    m: 0,
                    mt: 0.5,
                  }}
                >
                  Select at least one status to view data
                </FormHelperText>
              )}
            </FormControl>
          );
        })()}
        contentSx={{ overflow: "hidden" }}
      >
        {/* position: relative wrapper gives children a concrete height to resolve against. */}
        <Box sx={{ flex: 1, position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                bgcolor: panelBg,
                overflowX: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  width: "fit-content",
                  p: 1,
                }}
              >
                <BulkActionButton
                  label="Correct Vendor Data"
                  icon={<CheckIcon />}
                  baseDescription="Overwrite vendor data with e-Manifest values. Eligible rows will be marked as resolved."
                  eligibility={updateVendorDataEligibility}
                  totalSelected={selectedIds.size}
                  loading={mutationLoading}
                  action="updateVendorData"
                  setConfirmAction={setConfirmAction}
                />
                <BulkActionButton
                  label="Resolve in e-Manifest"
                  icon={<SyncIcon />}
                  baseDescription={
                    'Mark as being resolved in e-Manifest (e.g. updating the value in RCRAInfo). These will move to "Pending" until the change propagates back through the system.'
                  }
                  eligibility={resolveExternallyEligibility}
                  totalSelected={selectedIds.size}
                  loading={mutationLoading}
                  action="resolveExternally"
                  setConfirmAction={setConfirmAction}
                />
                <BulkActionButton
                  label="Non-Discrepancy"
                  icon={<CloseIcon />}
                  baseDescription="Mark as non-discrepancies. The difference is expected and will be hidden from view."
                  eligibility={nonDiscrepancyEligibility}
                  totalSelected={selectedIds.size}
                  loading={mutationLoading}
                  action="nonDiscrepancy"
                  setConfirmAction={setConfirmAction}
                />
              </Stack>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "row",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => {
                    if (v === "field" || v === "compliance") {
                      setActiveTab(v);
                    }
                  }}
                  sx={{ flexShrink: 0, borderBottom: 1, borderColor: "divider", px: 1 }}
                >
                  <Tab
                    value="compliance"
                    label={(() => {
                      // Reuse the card's band logic so the tab dot and card pill never disagree.
                      const overdue = filteredComplianceItems.filter(
                        (i) => getDeadlineBand(i, nowRef) === "crit"
                      ).length;
                      return (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {overdue > 0 && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: theme.palette.error.dark,
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <span>{`Compliance issues (${filteredComplianceItems.length})`}</span>
                        </Stack>
                      );
                    })()}
                  />
                  <Tab value="field" label={`Field discrepancies (${discrepancies.length})`} />
                </Tabs>

                {activeTab === "field" && (
                  <Box ref={tableScrollRef} sx={{ flex: 1, minWidth: 0, overflow: "auto" }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ px: 2, pt: 1.5, pb: 0.5 }}
                    >
                      Field discrepancies surface when a specific value disagrees between vendor
                      data, EPA e-Manifest, and the manifest document — e.g. waste codes, generator
                      info, quantity. Resolve them by updating the vendor record or acknowledging
                      the difference.
                    </Typography>
                    {loading ? (
                      <Stack spacing={2} sx={{ p: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox">
                                <Skeleton variant="rectangular" width={18} height={18} />
                              </TableCell>
                              {[
                                "Facility",
                                "Manifest",
                                "e-Manifest",
                                "Vendor",
                                "Document",
                                "Status",
                              ].map((h) => (
                                <TableCell key={h}>
                                  <Skeleton variant="text" width={80} />
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Array.from({ length: 8 }, (_, i) => (
                              <TableRow key={i}>
                                <TableCell padding="checkbox">
                                  <Skeleton variant="rectangular" width={18} height={18} />
                                </TableCell>
                                <TableCell>
                                  <Skeleton variant="text" width={120} />
                                  <Skeleton variant="text" width={80} />
                                </TableCell>
                                <TableCell>
                                  <Skeleton variant="text" width={100} />
                                </TableCell>
                                <TableCell>
                                  <Skeleton variant="text" width={140} />
                                </TableCell>
                                <TableCell>
                                  <Skeleton variant="text" width={140} />
                                </TableCell>
                                <TableCell>
                                  <Skeleton variant="text" width={60} />
                                </TableCell>
                                <TableCell>
                                  <Skeleton variant="rounded" width={80} height={24} />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Stack>
                    ) : (
                      <>
                        <TableContainer sx={{ overflow: "visible" }}>
                          <Table
                            size="small"
                            stickyHeader
                            sx={{
                              "& .MuiTableHead-root .MuiTableCell-head": {
                                top: 0,
                                // Override MUI's stickyHeader bg (background.default reads near-black in dark mode).
                                ...stickyOpaquePanelBg,
                                borderBottom: `1px solid ${theme.palette.background.paper}`,
                              },
                              // Top-align so multi-line values line up across the row.
                              "& .MuiTableBody-root .MuiTableCell-body": {
                                verticalAlign: "top",
                              },
                            }}
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox" />

                                <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>
                                  Facility
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>
                                  Manifest
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>
                                  e-Manifest
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>
                                  Vendor
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>
                                  Document
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>
                                  Status
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            {fieldGroups.map((group) => (
                              <TableBody key={group.field}>
                                <SectionHeaderRow
                                  label={group.label}
                                  count={group.items.length}
                                  colSpan={COL_COUNT}
                                  collapsed={collapsedGroups.has(group.field)}
                                  onToggle={() => toggleGroup(group.field)}
                                />
                                {!collapsedGroups.has(group.field) &&
                                  group.items.map(renderDiscrepancyRow)}
                              </TableBody>
                            ))}

                            {discrepancies.length === 0 && (
                              <TableBody>
                                <TableRow>
                                  <TableCell
                                    colSpan={COL_COUNT}
                                    align="center"
                                    sx={{
                                      py: 8,
                                      bgcolor: subtleSurfaceBg,
                                      borderBottom: "none",
                                    }}
                                  >
                                    <Typography variant="body2" color="text.secondary">
                                      {(() => {
                                        if (statusFilter.length === 0) {
                                          return "Select at least one status to view data";
                                        }
                                        if (statusFilter.length < ALL_DISCREPANCY_STATUSES.length) {
                                          return "No discrepancies found matching selected status";
                                        }
                                        if (isSingleManifest) {
                                          return "No discrepancies found for this manifest";
                                        }
                                        return "No discrepancies found for the selected manifests";
                                      })()}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            )}
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </Box>
                )}

                {activeTab === "compliance" && (
                  <Box sx={{ flex: 1, minWidth: 0, overflow: "auto" }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ px: 2, pt: 1.5, pb: 0.5 }}
                    >
                      Compliance issues are time-driven regulatory events — missing e-Manifests that
                      have crossed RCRA's 45- and 60-day thresholds, plus active EPA correction
                      requests with their own response deadlines. Each row has a deadline and a
                      specific action to resolve it.
                    </Typography>
                    <CompliancePane
                      items={filteredComplianceItems}
                      now={nowRef}
                      loading={loading}
                      activePreviewDocId={activePreviewDocId}
                      creatingExceptionReport={creatingExceptionReport}
                      onViewDocument={openDocumentPreview}
                      onResolveInEManifest={async (discrepancyId) => {
                        try {
                          await resolveExternally({
                            variables: { discrepancyIds: [discrepancyId] },
                          });
                          alerts.success("Marked as pending e-Manifest resolution");
                        } catch {
                          alerts.error("Failed to mark as pending");
                        }
                      }}
                      onAddProofOfContact={(pmId) => setShowProofOfContactForPmId(pmId)}
                      onCreateExceptionReport={async (pmId) => {
                        try {
                          await createExceptionReport({
                            variables: { processedManifestId: pmId },
                          });
                          alerts.success("Exception report submission created");
                        } catch {
                          alerts.error("Failed to create exception report submission");
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>

              {(previewDoc ?? previewLoading) && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        px: 2,
                        // Match the column header height so the preview top aligns with the first data row.
                        height: STICKY_COLUMN_HEADER_HEIGHT,
                        flexShrink: 0,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        bgcolor: subtleSurfaceBg,
                      }}
                    >
                      <Typography variant="subtitle2">Source Document</Typography>
                      <Stack direction="row" spacing={0.5}>
                        {previewDoc && (
                          <Tooltip title="Open in new tab">
                            <IconButton size="small" onClick={openDocumentInNewTab}>
                              <OpenInNewIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Close preview">
                          <IconButton size="small" onClick={() => setPreviewTarget(null)}>
                            <CloseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                    <Box sx={{ flex: 1, overflow: "hidden" }}>
                      {previewLoading ? (
                        <Skeleton
                          variant="rectangular"
                          animation="wave"
                          height="100%"
                          width="100%"
                        />
                      ) : previewDoc ? (
                        <FilePreview
                          fileUrl={previewDoc.url}
                          fileExtension={previewDoc.fileExtension}
                        />
                      ) : null}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Dialog>

      {showProofOfContactForPmId && (
        <ProofOfContactDialog
          open
          onClose={() => setShowProofOfContactForPmId(null)}
          processedManifestId={showProofOfContactForPmId}
        />
      )}

      <MuiDialog
        open={confirmAction !== null}
        onClose={() => {
          setConfirmAction(null);
          setConfirmNote("");
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{confirmAction?.label}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, whiteSpace: "pre-line" }}>
            {confirmAction?.description}
          </DialogContentText>
          <TextField
            label="Note (optional)"
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            size="small"
            value={confirmNote}
            onChange={(e) => setConfirmNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmAction(null);
              setConfirmNote("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={mutationLoading}
            onClick={handleConfirm}
          >
            {confirmAction?.label}
          </Button>
        </DialogActions>
      </MuiDialog>
    </>
  );
}
